import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, Button, CircularProgress, IconButton } from '@mui/material';
import { DownloadIcon, CloseIcon } from 'icons/materialIcons';
import Modal from './Modal';
import QRCodeStyling from 'qr-code-styling';
import { FRONTEND_BASE_URL } from '../../utils/apiUtils';

const QRCodeModal = ({ open, onClose, cardId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(false);

    const qrRef = useRef(null);
    const containerRef = useRef(null);
    const downloadQrRef = useRef(null);

    const BASE_URL = `${FRONTEND_BASE_URL}/card`;

    // Use useMemo to stabilize the URL and prevent constant regeneration
    const cardUrl = useMemo(() => {
        if (!cardId) return '';
        return `${BASE_URL}/${cardId}?source=qr_code`;
    }, [cardId, BASE_URL]);

    // Create display QR code - optimized for instant display
    useEffect(() => {
        if (!open || !cardId || !cardUrl) {
            return;
        }

        // Add a small delay to ensure the modal is fully rendered
        const initializeQR = () => {
            setTimeout(() => {
                try {
                    if (containerRef.current) {
                        // Clear any existing content
                        containerRef.current.innerHTML = '';

                        // Create new QR code instance
                        qrRef.current = new QRCodeStyling({
                            width: 250,
                            height: 250,
                            type: "svg",
                            data: cardUrl,
                            dotsOptions: {
                                color: '#000000',
                                type: "square"
                            },
                            cornersSquareOptions: {
                                type: "square"
                            },
                            cornersDotOptions: {
                                type: "square"
                            },
                            backgroundOptions: {
                                color: "#ffffff"
                            },
                            qrOptions: {
                                errorCorrectionLevel: "H"
                            },
                            margin: 0
                        });

                        // Correct way: QR code instance appends itself to the container
                        qrRef.current.append(containerRef.current);

                        // Verify the QR code was actually added
                        setTimeout(() => {
                            if (containerRef.current) {
                                if (containerRef.current.children.length === 0) {
                                    setError('Failed to display QR code');
                                }
                            }
                        }, 100);
                    }
                } catch (err) {
                    setError('Failed to generate QR code');
                }
            }, 100); // Small delay to ensure modal is rendered
        };

        setError('');
        initializeQR();

        // Cleanup function
        return () => {
            if (qrRef.current) {
                qrRef.current = null;
            }
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }
        };
    }, [open, cardUrl, cardId]);

    // Create download QR instance - only when modal opens
    useEffect(() => {
        if (!open || !cardUrl) return;

        downloadQrRef.current = new QRCodeStyling({
            width: 1000,
            height: 1000,
            type: "svg",
            data: cardUrl,
            dotsOptions: {
                color: '#000000',
                type: "square"
            },
            cornersSquareOptions: {
                type: "square"
            },
            cornersDotOptions: {
                type: "square"
            },
            backgroundOptions: {
                color: "#ffffff"
            },
            qrOptions: {
                errorCorrectionLevel: "H"
            },
            margin: 0
        });

        return () => {
            downloadQrRef.current = null;
        };
    }, [open, cardUrl, cardId]);

    const handleDownload = async () => {
        if (downloadQrRef.current && !downloading) {
            setDownloading(true);

            try {
                const fileName = `qr-code-${cardId}`;
                await downloadQrRef.current.download({
                    extension: 'png',
                    name: fileName,
                });
                setDownloading(false);
            } catch (error) {
                setError('Failed to download QR code');
                setDownloading(false);
            }
        }
    };

    const handleClose = () => {
        if (open) {
            setLoading(false);
            setError('');
            // Clear the container when closing
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }
        }
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            width="400px"
            height="auto"
        >
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 2,
                }}
            >
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                        '&:hover': {
                            bgcolor: 'grey.100'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Typography
                    variant="h6"
                    component="h2"
                    align="center"
                    fontWeight={600}
                    sx={{ mb: 3 }}
                >
                    QR Code
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 3,
                        minHeight: '250px',
                        minWidth: '250px',
                        position: 'relative'
                    }}
                >
                    {loading && (
                        <CircularProgress
                            size={40}
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 1
                            }}
                        />
                    )}

                    {error && (
                        <Typography color="error" align="center">
                            {error}
                        </Typography>
                    )}

                    <Box
                        ref={containerRef}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '250px',
                            height: '250px',
                            '& img, & svg, & canvas': {
                                display: 'block',
                                maxWidth: '100%',
                                maxHeight: '100%',
                            }
                        }}
                    />

                    {/* Fallback for debugging */}
                    {!loading && !error && open && cardUrl && containerRef.current?.innerHTML === '' && (
                        <Typography color="warning.main" align="center" sx={{ mt: 2, position: 'absolute', bottom: -30 }}>
                            QR Code loading... URL: {cardUrl}
                        </Typography>
                    )}
                </Box>

                <Button
                    variant="contained"
                    startIcon={downloading ? null : <DownloadIcon />}
                    onClick={handleDownload}
                    disabled={downloading || loading || !!error}
                    sx={{
                        backgroundColor: '#4f4f4f',
                        '&:hover': {
                            backgroundColor: '#666666',
                        },
                        textTransform: 'none',
                        px: 3,
                        py: 1,
                        minWidth: '180px'
                    }}
                >
                    {downloading ? (
                        <>
                            <CircularProgress
                                size={16}
                                sx={{
                                    color: '#fff',
                                    marginRight: 1
                                }}
                            />
                            Downloading...
                        </>
                    ) : (
                        'Download QR Code'
                    )}
                </Button>
            </Box>
        </Modal>
    );
};

export default QRCodeModal;